import React, { useCallback, useEffect, useRef, useState } from "react";
import { Point2D, Size, Position, AnyBlock } from "../types";
import { DEFAULT_GRID_RESOLUTION } from "../consts";
import {
  gridToPixelPosition,
  gridToPixelSize,
  pixelToGridPosition,
  pixelToGridPositionSloppy,
  pixelToGridSize,
  pixelToGridSizeSloppy,
} from "../utils/gridPosition";
import useThrottle from "../utils/useThrottle";
import { useCanvasRef } from "../jotai/atoms";
import { styled } from "../stitches.config";

enum Corners {
  "top-right" = "top-right",
  "bottom-right" = "bottom-right",
  "top-left" = "top-left",
  "bottom-left" = "bottom-left",
}

const BlockResizer = ({
  mousePositionRef,
  updateBlock,
  blockSize,
  blockPosition,
}: {
  mousePositionRef: React.MutableRefObject<Point2D> | undefined;
  updateBlock: (block: Partial<AnyBlock>, raise?: boolean) => void;
  blockSize: Size;
  blockPosition: Position;
}) => {
  const canvasRef = useCanvasRef();
  const updateBlockThrottled = useThrottle(updateBlock, 1000 / 30);
  const stateRef = useRef({
    blockSize: blockSize,
    blockPosition: blockPosition,
  });

  // Resize
  const [resizingCorner, setResizingCorner] = useState<Corners | null>();
  const resizeStartMouse = useRef<Point2D>({ x: 0, y: 0 });
  const resizeStartSize = useRef<Size>({ width: 0, height: 0 });
  const resizeStartPosition = useRef<Point2D>({ x: 0, y: 0 });

  stateRef.current.blockSize = blockSize;
  stateRef.current.blockPosition = blockPosition;

  const handleResizeStart = useCallback(
    (corner: Corners, e: React.MouseEvent) => {
      const { blockSize, blockPosition } = stateRef.current;

      if (!mousePositionRef) return;
      e.stopPropagation(); // Prevent activating move
      e.preventDefault();

      const blockPixelSize = gridToPixelSize(blockSize);
      const blockPixelPos = gridToPixelPosition(blockPosition);

      resizeStartMouse.current.x = mousePositionRef.current.x;
      resizeStartMouse.current.y = mousePositionRef.current.y;

      resizeStartPosition.current.x = blockPixelPos.x;
      resizeStartPosition.current.y = blockPixelPos.y;

      resizeStartSize.current.width = blockPixelSize.width;
      resizeStartSize.current.height = blockPixelSize.height;

      setResizingCorner(corner);
    },
    [mousePositionRef, stateRef]
  );

  useEffect(() => {
    if (!mousePositionRef || !canvasRef) return;

    function handleResize({ sloppy = false, throttled = false } = {}) {
      const { blockSize, blockPosition } = stateRef.current;

      if (!resizingCorner || !resizeStartSize.current || !mousePositionRef) {
        return;
      }

      const newSize = gridToPixelSize(blockSize);
      const newPosition = gridToPixelPosition(blockPosition);

      const localPixelToGridSize = sloppy
        ? pixelToGridSizeSloppy
        : (size: Size) => pixelToGridSize(size, Math.round);

      const localPixelToGridPosition = sloppy
        ? pixelToGridPositionSloppy
        : (position: Position) => pixelToGridPosition(position, Math.round);

      const localUpdateBlock = throttled ? updateBlockThrottled : updateBlock;

      const mx = mousePositionRef.current.x;
      const my = mousePositionRef.current.y;

      const x1 = resizeStartPosition.current.x;
      const y1 = resizeStartPosition.current.y;
      const x2 = x1 + resizeStartSize.current.width;
      const y2 = y1 + resizeStartSize.current.height;

      const minPixelSize = DEFAULT_GRID_RESOLUTION * 2;

      switch (resizingCorner) {
        case Corners["bottom-right"]:
          // pin top left
          const pinnedCorner1 = { x: x1, y: y1 };
          newSize.width = Math.max(minPixelSize, mx - pinnedCorner1.x);
          newSize.height = Math.max(minPixelSize, my - pinnedCorner1.y);
          newPosition.x = pinnedCorner1.x;
          newPosition.y = pinnedCorner1.y;
          break;
        case Corners["top-right"]:
          // pin bottom left
          const pinnedCorner2 = { x: x1, y: y2 };
          newSize.width = Math.max(minPixelSize, mx - pinnedCorner2.x);
          newSize.height = Math.max(minPixelSize, pinnedCorner2.y - my);
          newPosition.x = pinnedCorner2.x;
          newPosition.y = pinnedCorner2.y - newSize.height;
          break;
        case Corners["bottom-left"]:
          // pin top right
          const pinnedCorner3 = { x: x2, y: y1 };
          newSize.width = Math.max(minPixelSize, pinnedCorner3.x - mx);
          newSize.height = Math.max(minPixelSize, my - pinnedCorner3.y);
          newPosition.x = pinnedCorner3.x - newSize.width;
          newPosition.y = pinnedCorner3.y;
          break;
        case Corners["top-left"]:
          // pin bottom right
          const pinnedCorner4 = { x: x2, y: y2 };
          newSize.width = Math.max(minPixelSize, pinnedCorner4.x - mx);
          newSize.height = Math.max(minPixelSize, pinnedCorner4.y - my);
          newPosition.x = pinnedCorner4.x - newSize.width;
          newPosition.y = pinnedCorner4.y - newSize.height;
          break;
      }

      localUpdateBlock(
        {
          size: localPixelToGridSize(newSize),
          position: localPixelToGridPosition(newPosition),
        },
        false
      );
    }

    function onMouseMove(e: MouseEvent) {
      if (resizingCorner) {
        e.preventDefault(); // Prevent drag selecting
      }

      handleResize({ sloppy: true, throttled: true });
    }

    function onMouseUp() {
      handleResize({ sloppy: false, throttled: false });
      setResizingCorner(null);
    }

    if (resizingCorner) {
      canvasRef.addEventListener("mousemove", onMouseMove);
      canvasRef.addEventListener("mouseup", onMouseUp);
    }

    return () => {
      canvasRef.removeEventListener("mousemove", onMouseMove);
      canvasRef.removeEventListener("mouseup", onMouseUp);
    };
  }, [
    canvasRef,
    resizingCorner,
    updateBlock,
    mousePositionRef,
    updateBlockThrottled,
    stateRef,
  ]);

  return (
    <>
      {Object.keys(Corners).map((corner) => (
        <Resize
          key={corner}
          onMouseDown={(e: React.MouseEvent<Element, MouseEvent>) =>
            handleResizeStart(corner as Corners, e)
          }
          active={resizingCorner === corner}
          data-cursor={
            corner === "bottom-right" || corner === "top-left"
              ? "nwse-resize"
              : "nesw-resize"
          }
          corner={corner as Corners}
        />
      ))}
    </>
  );
};

export const Resize = styled("div", {
  position: "absolute",
  width: "$4",
  height: "$4",
  backgroundColor: "transparent",
  pointerEvents: "auto",

  variants: {
    active: {
      true: {
        transform: "scale(4)",
      },
    },
    corner: {
      "bottom-right": {
        bottom: "-$1",
        right: "-$1",
      },

      "top-right": {
        top: "-$1",
        right: "-$1",
      },

      "bottom-left": {
        bottom: "-$1",
        left: "-$1",
      },

      "top-left": {
        top: "-$1",
        left: "-$1",
      },
    },
  },
});

export default React.memo(BlockResizer);
